import { createLocalUserRequestSchema, RoleCode } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import { useCreateLocalUser } from '@/platform/features/users/use-users';

const EMPTY = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  departmentId: '',
};

export function CreateLocalUserDialog({
  open,
  departments,
  onClose,
}: {
  open: boolean;
  departments: readonly { id: string; name: string }[];
  onClose: () => void;
}) {
  const create = useCreateLocalUser();
  const [form, setForm] = useState(EMPTY);

  function close() {
    setForm(EMPTY);
    onClose();
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const parsed = createLocalUserRequestSchema.safeParse({
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      password: form.password,
      departmentId: form.departmentId || null,
      role: RoleCode.END_USER,
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Bitte die Angaben prüfen.');
      return;
    }
    try {
      await create.mutateAsync(parsed.data);
      toast.success('Lokaler Benutzer angelegt. Der Chat läuft im Portal, nicht in Teams.');
      close();
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Anlegen fehlgeschlagen.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Lokalen Benutzer anlegen</DialogTitle>
          <DialogDescription>
            Konto mit Passwort und ohne Microsoft-Anmeldung. Nachrichten an diese Person laufen im
            Portal-Chat.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={(event) => void onSubmit(event)}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="local-first-name">Vorname</Label>
              <Input
                id="local-first-name"
                value={form.firstName}
                onChange={(event) =>
                  setForm((current) => ({ ...current, firstName: event.target.value }))
                }
                autoComplete="off"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="local-last-name">Nachname</Label>
              <Input
                id="local-last-name"
                value={form.lastName}
                onChange={(event) =>
                  setForm((current) => ({ ...current, lastName: event.target.value }))
                }
                autoComplete="off"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="local-email">E-Mail</Label>
            <Input
              id="local-email"
              type="email"
              value={form.email}
              onChange={(event) =>
                setForm((current) => ({ ...current, email: event.target.value }))
              }
              autoComplete="off"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="local-password">Passwort</Label>
            <Input
              id="local-password"
              type="password"
              value={form.password}
              onChange={(event) =>
                setForm((current) => ({ ...current, password: event.target.value }))
              }
              autoComplete="new-password"
            />
            <p className="text-xs text-muted-foreground">
              Mindestens 12 Zeichen, Groß- und Kleinbuchstabe und eine Ziffer.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="local-department">Abteilung</Label>
            <Select
              value={form.departmentId || 'none'}
              onValueChange={(value) =>
                setForm((current) => ({ ...current, departmentId: value === 'none' ? '' : value }))
              }
            >
              <SelectTrigger id="local-department" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Keine Abteilung</SelectItem>
                {departments.map((department) => (
                  <SelectItem key={department.id} value={department.id}>
                    {department.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? (
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
              ) : null}
              Anlegen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
